import 'C:\\Projects\\genkei\\zumeco\\static\\doodle\\view\\spreadsheetData.ts.esbuild-plugin-windicss.css';
import equal from 'fast-deep-equal';
import type { Writable } from 'ts-essentials';
import { Luckysheet, LuckysheetBorderInfo, LuckysheetCell, LuckysheetCells, LuckysheetSheet } from '../../../server/src/luckysheet-models';
import { SpreadsheetSheet } from '../../../server/src/models';
export const emptySpreadsheetSheet = (): SpreadsheetSheet => ({
  calcChain: {},
  cells: {},
  columnHidden: {},
  columnWidth: {},
  frozen: {},
  horizontalBorders: {},
  hyperlink: {},
  merge: {},
  rowHeight: {},
  rowHidden: {},
  verticalBorders: {}
});
const compressBorderInfo = (borderInfo?: readonly LuckysheetBorderInfo[]): Pick<SpreadsheetSheet, 'horizontalBorders' | 'verticalBorders'> => {
  if (!borderInfo) {
    return {
      verticalBorders: {},
      horizontalBorders: {}
    };
  }
  const v: Writable<SpreadsheetSheet['verticalBorders']> = {};
  const h: Writable<SpreadsheetSheet['horizontalBorders']> = {};
  for (const border of borderInfo) {
    if (border.rangeType === 'cell') {
      const {
        row_index,
        col_index,
        l,
        t,
        r,
        b
      } = border.value;
      l !== undefined && (v[`${row_index}_${col_index}`] = l === null ? null : [l.style, rgb2hex(l.color)]);
      t !== undefined && (h[`${row_index}_${col_index}`] = t === null ? null : [t.style, rgb2hex(t.color)]);
      r !== undefined && (v[`${row_index}_${col_index + 1}`] = r === null ? null : [r.style, rgb2hex(r.color)]);
      b !== undefined && (h[`${row_index + 1}_${col_index}`] = b === null ? null : [b.style, rgb2hex(b.color)]);
      continue;
    }
    const borderValue = border.borderType === 'border-none' ? null : [+border.style, rgb2hex(border.color)] as const;
    const setBorderLeft = ([firstRow, lastRow]: readonly [number, number], column: number) => {
      for (let row = firstRow; row <= lastRow; row++) {
        v[`${row}_${column}`] = borderValue;
      }
    };
    const setBorderLefts = (rowRange: readonly [number, number], firstColumn: number, lastColumn: number) => {
      for (let column = firstColumn; column <= lastColumn; column++) {
        setBorderLeft(rowRange, column);
      }
    };
    const setBorderTop = ([firstColumn, lastColumn]: readonly [number, number], row: number) => {
      for (let column = firstColumn; column <= lastColumn; column++) {
        h[`${row}_${column}`] = borderValue;
      }
    };
    const setBorderTops = (columnRange: readonly [number, number], firstRow: number, lastRow: number) => {
      for (let row = firstRow; row <= lastRow; row++) {
        setBorderTop(columnRange, row);
      }
    };
    for (const {
      row,
      column
    } of border.range) {
      switch (border.borderType) {
        case 'border-left':
          setBorderLeft(row, column[0]);
          break;
        case 'border-top':
          setBorderTop(column, row[0]);
          break;
        case 'border-right':
          setBorderLeft(row, column[1] + 1);
          break;
        case 'border-bottom':
          setBorderTop(column, row[1] + 1);
          break;
        case 'border-outside':
          setBorderLeft(row, column[0]);
          setBorderTop(column, row[0]);
          setBorderLeft(row, column[1] + 1);
          setBorderTop(column, row[1] + 1);
          break;
        case 'border-inside':
          setBorderLefts(row, column[0] + 1, column[1]);
          setBorderTops(column, row[0] + 1, row[1]);
          break;
        case 'border-vertical':
          setBorderLefts(row, column[0] + 1, column[1]);
          break;
        case 'border-horizontal':
          setBorderTops(column, row[0] + 1, row[1]);
          break;
        case 'border-all':
        case 'border-none':
          setBorderLefts(row, column[0], column[1] + 1);
          setBorderTops(column, row[0], row[1] + 1);
          break;
      }
    }
  }
  return {
    verticalBorders: v,
    horizontalBorders: h
  };
};
const decompressBorderInfo = (borders: SpreadsheetSheet): LuckysheetBorderInfo[] => {
  const borderInfo: LuckysheetBorderInfo[] = [];
  for (const [rc, border] of Object.entries(borders.verticalBorders ?? []) as [`${number}_${number}`, undefined | null | readonly [number, string]][]) {
    const [row_index, col_index] = rc.split('_').map(Number);
    const h = borders.horizontalBorders?.[rc];
    border && borderInfo.push({
      rangeType: 'cell',
      value: {
        row_index,
        col_index,
        l: {
          style: border[0],
          color: border[1]
        },
        t: h ? {
          style: h[0],
          color: h[1]
        } : undefined
      }
    });
  }
  for (const [rc, border] of Object.entries(borders.horizontalBorders ?? []) as [`${number}_${number}`, undefined | null | readonly [number, string]][]) {
    if (!borders.verticalBorders?.[rc]) {
      const [row_index, col_index] = rc.split('_').map(Number);
      border && borderInfo.push({
        rangeType: 'cell',
        value: {
          row_index,
          col_index,
          t: {
            style: border[0],
            color: border[1]
          }
        }
      });
    }
  }
  return borderInfo;
};
const rgb2hex = (color: string) => {
  const rgb = color.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/);
  if (!rgb) {
    return color;
  }
  const hex = (+rgb[1]).toString(16).padStart(2, '0') + (+rgb[2]).toString(16).padStart(2, '0') + (+rgb[3]).toString(16).padStart(2, '0');
  if (hex.length !== 6) {
    return color;
  }
  // eslint-disable-next-line @typescript-eslint/prefer-string-starts-ends-with
  if (hex[0] === hex[1] && hex[2] === hex[3] && hex[4] === hex[5]) {
    return `#${hex[0]}${hex[2]}${hex[4]}`;
  } else {
    return `#${hex}`;
  }
};
const compressCell = (cell: LuckysheetCell | null | undefined) => {
  if (!cell) {
    return;
  }
  const c = {
    ...cell
  };
  delete c.ff;
  c.bl === 0 && delete c.bl;
  c.it === 0 && delete c.it;
  c.cl === 0 && delete c.cl;
  c.ht == 1 && delete c.ht;
  c.vt == 0 && delete c.vt;
  c.fs == 10 && delete c.fs;
  c.m === '' && delete c.m;
  c.v === '' && delete c.v;
  c.fc && (c.fc = rgb2hex(c.fc));
  c.bg && (c.bg = rgb2hex(c.bg));
  c.ct && equal(c.ct, {
    fa: 'General',
    t: 'g'
  }) && delete c.ct;
  Object.entries(c).forEach(([k, v]) => v === null && delete c[k as keyof LuckysheetCell]);
  return Object.keys(c).length === 0 ? undefined : c;
};
const compressCells = (luckysheetCells: LuckysheetCells) => {
  const cells: Writable<SpreadsheetSheet['cells']> = {};
  luckysheetCells.forEach((luckysheetRow, rowIndex) => {
    luckysheetRow.forEach((luckysheetCell, columnIndex) => {
      const cell = compressCell(luckysheetCell);
      cell && (cells[`${rowIndex}_${columnIndex}`] = cell);
    });
  });
  return cells;
};
const decompressCell = (cell: LuckysheetCell | null | undefined): LuckysheetCell | null => cell ? {
  ...cell,
  ct: cell.ct ?? {
    fa: 'General',
    t: 'g'
  }
} : null;
const decompressCells = (cells: SpreadsheetSheet['cells'], rows: number, columns: number) => {
  const luckysheetCells: LuckysheetCells = [];
  const indices = Object.keys(cells ?? {}).map(rc => rc.split('_').map(n => parseInt(n, 10)) as [number, number]);
  rows = Math.max(rows, ...indices.map(([row]) => row + 1));
  columns = Math.max(columns, ...indices.map(([, column]) => column + 1));
  for (let r = 0; r < rows; r++) {
    const row: (LuckysheetCell | null)[] = [];
    for (let c = 0; c < columns; c++) {
      row[c] = decompressCell(cells?.[`${r}_${c}`]);
    }
    luckysheetCells.push(row);
  }
  return luckysheetCells;
};
export const compressSpreadsheetSheet = ({
  calcChain,
  config,
  data,
  freezen,
  hyperlink
}: LuckysheetSheet): SpreadsheetSheet => {
  const frozenRow = freezen?.horizontal?.freezenhorizontaldata[1] ?? null;
  const frozenColumn = freezen?.vertical?.freezenverticaldata[1] ?? null;
  return {
    ...compressBorderInfo(config?.borderInfo),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    calcChain: calcChain ? Object.fromEntries(calcChain.map(({
      r,
      c,
      index,
      ...rest
    }) => [`${r}_${c}`, rest])) : {},
    cells: compressCells(data),
    columnHidden: config?.colhidden ?? {},
    columnWidth: config?.columnlen ?? {},
    frozen: frozenRow === null && frozenColumn === null ? {} : {
      row: frozenRow,
      column: frozenColumn
    },
    hyperlink: hyperlink ?? {},
    merge: config?.merge ?? {},
    rowHidden: config?.rowhidden ?? {},
    rowHeight: config?.rowlen ?? {}
  };
};
declare const luckysheet: Luckysheet;
const shallowDiff = <T extends object,>(oldObj: T | null | undefined, newObj: T): undefined | { [P in keyof T]?: T[P] | null } => {
  if (!oldObj) {
    return newObj;
  }
  const diff: { [P in keyof T]?: T[P] | null } = {};
  for (const [key, newValue] of newObj ? Object.entries(newObj) : []) {
    if (!equal(oldObj[key as keyof T] ?? null, newObj[key as keyof T] ?? null)) {
      diff[key as keyof T] = newValue as T[keyof T];
    }
  }
  for (const key of Object.keys(oldObj)) {
    if (!newObj || !Object.prototype.hasOwnProperty.call(newObj, key)) {
      diff[key as keyof T] = null;
    }
  }
  return Object.keys(diff).length === 0 ? undefined : diff;
};
const deleteNullOrUndefinedProperties = <T extends object,>(obj: { [P in keyof T]?: T[P] | null }): { [P in keyof T]?: T[P] } => {
  for (const [key, value] of Object.entries(obj)) {
    if (value === null || value === undefined) {
      delete obj[key as keyof T];
    }
  }
  return obj as { [P in keyof T]?: T[P] };
};
export const diffSpreadsheetSheetData = (oldSheet: SpreadsheetSheet, newSheet: SpreadsheetSheet): SpreadsheetSheet | undefined => {
  const entries = ([...Object.keys(oldSheet), ...Object.keys(newSheet)] as (keyof SpreadsheetSheet)[]).filter((e, i, a) => a.indexOf(e) === i).map(key => [key, shallowDiff<any>(oldSheet[key], newSheet[key])]).filter(([, value]) => value !== undefined);
  return entries.length ? Object.fromEntries(entries) as SpreadsheetSheet : undefined;
};
export const mergeSpreadsheetSheetData = (sheet: SpreadsheetSheet, diff: SpreadsheetSheet): SpreadsheetSheet => {
  const entries = ([...Object.keys(sheet), ...Object.keys(diff)] as (keyof SpreadsheetSheet)[]).filter((e, i, a) => a.indexOf(e) === i).map(key => [key, deleteNullOrUndefinedProperties({
    ...sheet[key],
    ...diff[key]
  } as object)]).filter(([, value]) => value !== undefined);
  return Object.fromEntries(entries) as SpreadsheetSheet;
};
export const pushSpreadsheetSheetData = (sheet: SpreadsheetSheet) => {
  sheet = structuredClone(sheet);
  const luckysheetSheet = luckysheet.getSheet() as Writable<LuckysheetSheet>;
  luckysheet.store.flowdata = decompressCells(sheet.cells, luckysheetSheet.data.length, luckysheetSheet.data[0]?.length ?? 80);
  luckysheetSheet.calcChain = sheet.calcChain && Object.entries(sheet.calcChain).map(([rc, rest]) => {
    const [r, c] = rc.split('_');
    return {
      r: +r,
      c: +c,
      index: luckysheetSheet.index,
      ...rest
    };
  });
  luckysheet.setConfig({
    ...luckysheetSheet.config,
    borderInfo: decompressBorderInfo(sheet),
    columnlen: sheet.columnWidth && deleteNullOrUndefinedProperties(sheet.columnWidth),
    colhidden: sheet.columnHidden && deleteNullOrUndefinedProperties(sheet.columnHidden),
    rowlen: sheet.rowHeight && deleteNullOrUndefinedProperties(sheet.rowHeight),
    rowhidden: sheet.rowHidden && deleteNullOrUndefinedProperties(sheet.rowHidden),
    merge: sheet.merge && deleteNullOrUndefinedProperties(sheet.merge)
  });
  luckysheet.hyperlinkController.hyperlink = luckysheetSheet.hyperlink = sheet.hyperlink && deleteNullOrUndefinedProperties(sheet.hyperlink);
  if (sheet.frozen) {
    const {
      row,
      column
    } = sheet.frozen;
    if (row && column) {
      luckysheet.setBothFrozen(true, {
        range: {
          row_focus: row - 1,
          column_focus: column - 1
        }
      });
    } else if (row) {
      luckysheet.setHorizontalFrozen(true, {
        range: {
          row_focus: row - 1
        }
      });
    } else if (column) {
      luckysheet.setVerticalFrozen(true, {
        range: {
          column_focus: column - 1
        }
      });
    } else {
      luckysheet.cancelFrozen();
    }
  }

  // 拡大率を小さくした状態で refresh() すると、スクロール位置が意図せず変わってしまう不具合の修正（原因の特定には至っておらず対症療法）
  $('#luckysheet-scrollbar-x').scrollLeft($('#luckysheet-cell-main').scrollLeft()!);
  $('#luckysheet-scrollbar-y').scrollTop($('#luckysheet-cell-main').scrollTop()!);
  luckysheet.refresh();
};